import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { Banner, DataRow, EmptyState, RowButton, StatusChip } from "../../components/mui";
import { SkeletonRows } from "../../components/loading";
import { reviewStatusKey, reviewTypeLabel, REVIEW_STATUS_LABEL } from "../../design/domain";
import { MdCheck, MdInfoOutline, MdWarningAmber } from "react-icons/md";

/* ===================== 取值工具 ===================== */

/**
 * 後端各模組的欄位命名不一致（snake_case / camelCase / PascalCase 都有），
 * 而簽核 API 由另一批人同時開發中，這裡一次吸收所有可能的命名，
 * 任一個都沒有時回 undefined，由呼叫端決定 fallback。
 */
function pick(obj, ...keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const v = _.get(obj, keys[i]);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

/** 到今天的天數差（以「日」為單位，忽略時分秒，避免同一天被算成 0.4 天） */
function dayDiff(due) {
  if (!due) return null;
  const d = due instanceof Date ? due : new Date(String(due).replace(/\//g, "-"));
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  const a = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const b = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((b - a) / 86400000);
}

/**
 * 從「送出審核」到今天等了幾天。
 * dayDiff 算的是「還剩幾天」（未來為正），送出時間一定在過去，所以取負值。
 * 送出時間取不到就回 null —— 沒有資料就不要編一句「等 0 天」出來。
 */
function waitedDays(row) {
  const d = dayDiff(
    pick(
      row,
      "opened_at",
      "openedAt",
      "submit_at",
      "submitted_at",
      "create_at",
      "created_at",
      "createAt",
      "CreateAt",
      "OpenedAt"
    )
  );
  if (d === null) return null;
  return Math.max(0, -d);
}

/**
 * 四個語氣。設計稿不讓畫面寫死顏色，顏色全部交給 StatusChip 的 kind，
 * 原本的四色 SLA 燈號（紅／橘／綠／灰）一對一換成這四個修飾詞。
 * 寫死 hex 的版本在深色主題下不會跟著換色，那正是舊配色不一致的來源。
 */
const TONE_ATTENTION = "attention"; // 原 SLA_RED：逾期、今日到期
const TONE_WARN = "warn"; //           原 SLA_AMBER：剩 1~2 天
const TONE_PROGRESS = "progress"; //   原 SLA_GREEN：還有時間
const TONE_CLOSED = "closed"; //       原 SLA_GREY：沒有到期日，無從判斷
const TONE_DONE = "done"; //           審核已通過（設計稿 L1213 的 ✓ 通過）

/**
 * 後端若自己算好燈號，回的是舊版前端約定的那四個字面 hex。
 * 這裡把它換成語氣；不在表上的顏色當作看不懂，改由到期日自己算，
 * 不要把來路不明的色碼直接塗到畫面上。
 */
const HEX_TONE = {
  "#d92d20": TONE_ATTENTION,
  "#dc6803": TONE_WARN,
  "#0e7c66": TONE_PROGRESS,
  "#8a877e": TONE_CLOSED,
};

/**
 * 審核狀態 → 標籤語氣。
 * 通過用 done（設計稿 L1213 就是那顆「✓ 通過」），被退回的要看得出來得回去改，
 * 所以跟逾期用同一個語氣；還在等的用 warn。
 */
const ST_TONE = {
  pending: TONE_WARN,
  approved: TONE_DONE,
  rejected: TONE_ATTENTION,
  returned: TONE_ATTENTION,
};

/** 一次顯示幾筆；其餘按「看其他 N 件」再展開（設計稿 L855 的長清單作法） */
const PAGE_SIZE = 10;

/** 分頁鍵：同時也是網址 ?tab= 的合法值，其他值一律當作沒帶 */
const TAB_TOME = "tome";
const TAB_BYME = "byme";
const TAB_KEYS = [TAB_TOME, TAB_BYME];

/**
 * SLA 燈號。後端若已算好（sla / slaColor）就直接沿用，沒給時由到期日自算，
 * 讓 API 尚未上線時這一欄也不會開天窗。
 * 對應設計稿：剩 3 天以上=還有時間、剩 1~2 天=該注意、今日到期或逾期=要處理。
 */
function slaOf(row) {
  const text = pick(row, "sla", "sla_text", "slaText", "Sla");
  const color = pick(row, "sla_color", "slaColor", "slaC", "SlaColor");
  const tone = HEX_TONE[String(color).toLowerCase()];
  if (text && tone) return { text: String(text), tone };

  const d = dayDiff(pick(row, "sla_due", "slaDue", "due_date", "dueDate", "deadline", "DueDate"));
  if (d === null) return text ? { text: String(text), tone: tone || TONE_CLOSED } : null;
  if (d < 0) return { text: "逾期", tone: TONE_ATTENTION };
  if (d === 0) return { text: "今日到期", tone: TONE_ATTENTION };
  return { text: `剩 ${d} 天`, tone: d >= 3 ? TONE_PROGRESS : TONE_WARN };
}

/** 沒有到期日時改用「等了幾天」判語氣：三天沒人看就該有人催了（設計稿把「等 3 天了」畫成 attention） */
function toneByWait(days) {
  if (days >= 3) return TONE_ATTENTION;
  if (days >= 1) return TONE_WARN;
  return TONE_PROGRESS;
}

/**
 * 「等了幾天」—— 這一頁最關鍵的一個改動。
 *
 * 舊版這一欄寫的是「剩 3 天」，使用者得自己記得今天幾號、對方什麼時候送的，
 * 才知道這件事到底擺多久了。設計稿把結論直接寫出來：「等 3 天了」。
 *
 * 顏色仍然聽 SLA 的（到期日才是真正的急迫度），SLA 取不到時才用等待天數自己判。
 * 連送出時間都沒有就整個退回 SLA 的說法，這一欄才不會開天窗。
 */
function waitOf(row) {
  const sla = slaOf(row);
  const days = waitedDays(row);
  if (days === null) return sla ? { ...sla, days: null } : null;
  return {
    text: days === 0 ? "今日送審" : `已等待 ${days} 天`,
    tone: sla ? sla.tone : toneByWait(days),
    days,
  };
}

/**
 * 把關者可能是字串、字串陣列或物件陣列，統一併成一行。
 * `assignees` 是後端既有審核 DTO（ReviewController/List）實際使用的欄位名，
 * 其元素為 { reviewer_id, decision, ... }，因此一併納入。
 */
function reviewersOf(row) {
  const v = pick(
    row,
    "reviewer_names",
    "reviewerNames",
    "reviewers",
    "reviewer_name",
    "reviewerName",
    "assignees",
    "reviewer_accounts",
    "reviewerAccounts",
    "rv",
    "Reviewers"
  );
  if (v === undefined) return "";
  if (Array.isArray(v)) {
    const names = v
      .map((x) =>
        typeof x === "string"
          ? x
          : pick(
              x,
              "name",
              "reviewer_name",
              "reviewerName",
              "reviewer_id",
              "reviewerId",
              "account",
              "Account"
            ) || ""
      )
      .filter(Boolean);
    return names.join("、");
  }
  return String(v);
}

/** 工項 id：整列點下去要靠它導到 /Approvals/{taskId} */
function taskIdOf(row) {
  return pick(row, "task_id", "taskId", "TaskId") ?? null;
}

/**
 * 待我審核的原始資料（state.Approval.ToMe）→ 清單列。
 * 個人工項首頁的「待我審核」分頁也用這一份，兩頁的列才會講一樣的話。
 */
export function buildToMeRows(toMeRaw) {
  const list = [toMeRaw, _.get(toMeRaw, "data_list"), _.get(toMeRaw, "list"), _.get(toMeRaw, "items")].find(Array.isArray) || [];
  return list.map((r, i) => {
    const by = String(
      pick(
        r,
        "opened_by_name",
        "openedByName",
        "requester_name",
        "requesterName",
        "opened_by",
        "openedBy",
        "by",
        "OpenedBy"
      ) || ""
    );
    const w = waitOf(r);
    const reviewId = pick(r, "id", "review_id", "reviewId", "Id");

    /*
     * 欄位順序與全站清單一致：人 → 等多久了。缺哪一項就不放那一格，不留空位。
     * 這裡不放工項代碼：設計稿的審核列沒有代碼，代碼只出現在工項詳情頁
     * （Approval/View.js 已經有），而每一頁 meta 的第一格固定是「誰」——
     * 換頁時眼睛才不必重新找。
     */
    const metas = [];
    if (by) metas.push(`${by} 送出`);
    if (w && w.days !== null) {
      metas.push(w.days === 0 ? "今日送審" : `已送審 ${w.days} 天，尚未審核`);
    }

    return {
      key: reviewId != null ? `ap-${reviewId}` : `ap-${i}`,
      taskId: taskIdOf(r),
      title: String(pick(r, "task_title", "taskTitle", "title", "name", "Title") || "—"),
      type: reviewTypeLabel(pick(r, "review_type", "reviewType", "type", "ReviewType")),
      metas,
      tag: w ? { text: w.text, tone: w.tone, mark: w.tone === TONE_ATTENTION ? <MdWarningAmber /> : null } : null,
      attention: !!w && w.tone === TONE_ATTENTION,
    };
  });
}


/**
 * 「9 月 12 日」。
 * domain 的 fmtDate 回的是 2026/9/12，是舊畫面的格式；新設計一律把日期寫成中文
 * （設計稿 L1213「9 月 12 日通過」），兩種格式不混用。
 */
function fmtDay(v) {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(String(v).replace(/\//g, "-"));
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日`;
}

/* ===================== 一列 ===================== */

/**
 * 一筆審核 —— 照抄設計稿 L1195-L1205 的清單列。
 *
 * 整列就是那顆按鈕（點哪裡都會開），所以裡面一律用 <span>：
 * <button> 裡不能再放 <button>，也不該放 <div>。
 * 舊版列尾那兩顆「通過／要求修改」因此不在這裡，改到審核整頁上按（見檔尾說明）。
 */
function ApprovalRow({ row, onOpen }) {
  const main = (
    <Box component="span" sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Stack component="span" direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <StatusChip kind="type" size="small" component="span">
          {row.type}
        </StatusChip>
        <Typography component="span" variant="subtitle1">
          {row.title}
        </Typography>
      </Stack>
      <Typography
        component="span"
        variant="body2"
        color="text.secondary"
        sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}
      >
        {row.metas.map((m, i) => (
          <span key={`${row.key}-m${i}`}>{m}</span>
        ))}
      </Typography>
    </Box>
  );

  const side = row.tag ? (
    <StatusChip kind={row.tag.tone} mark={row.tag.mark} component="span">
      {row.tag.text}
    </StatusChip>
  ) : null;

  /*
   * 沒有工項編號就沒有地方可以去。這一筆仍然要列出來（不能讓一筆審核憑空消失），
   * 但不做成按鈕 —— 按了沒反應比看起來就不能按更難解釋。
   * 用 DataRow（RowButton 的靜態版：同樣的內距、分隔線、左側色帶），只是不可點、沒有箭頭。
   */
  if (row.taskId == null) {
    return (
      <DataRow tone={row.attention ? "attention" : ""} side={side}>
        {main}
      </DataRow>
    );
  }

  return (
    <RowButton tone={row.attention ? "attention" : ""} side={side} onClick={() => onOpen(row)}>
      {main}
    </RowButton>
  );
}

ApprovalRow.propTypes = {
  row: PropTypes.shape({
    key: PropTypes.string,
    taskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    type: PropTypes.string,
    metas: PropTypes.arrayOf(PropTypes.string),
    tag: PropTypes.shape({
      text: PropTypes.string,
      tone: PropTypes.string,
      mark: PropTypes.node,
    }),
    attention: PropTypes.bool,
  }),
  onOpen: PropTypes.func,
};

ApprovalRow.defaultProps = {
  row: { key: "", taskId: null, title: "", type: "", metas: [], tag: null, attention: false },
  onOpen: () => {},
};

/* ===================== 頁面 ===================== */

/**
 * 審核清單 — 照抄設計稿 el-pm-template_4.html:1190-1239（#screen-approvals）。
 *
 * 兩份清單合在一頁、用分頁列切換：有東西的那一份排前面也預設選中，
 * 並用一條 info 提示條說明「為什麼現在看到的是這一份」——
 * 不寫的話使用者會以為自己點錯了分頁，或以為另一份不見了。
 *
 * 每一列點下去是整頁 /Approvals/{taskId}，不再開抽屜：
 * 審核要比對「當初要求什麼」和「他做了什麼」，720px 的抽屜塞不下兩欄並排。
 */
function Approvals(props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  /*
   * 兩份清單「第一次載入完成」的旗標。store 的預設值就是空陣列，
   * 光看長度分不出「還在載」和「真的沒有」——不分的話空狀態會先閃一下，
   * 自動選分頁也會在資料回來前就誤判成「等我審的是空的」。
   * saga 在 finally 才呼叫 loading(false)，失敗時也會走到，不會卡在載入中。
   */
  const [toMeReady, setToMeReady] = useState(false);
  const [byMeReady, setByMeReady] = useState(false);
  /* 各分頁目前顯示到第幾筆（「看其他 N 件」會往上加）。兩份各記各的，切回來不會被重設 */
  const [shown, setShown] = useState({ [TAB_TOME]: PAGE_SIZE, [TAB_BYME]: PAGE_SIZE });

  const { GET_ApprovalToMe, GET_ApprovalByMe } = props;

  const fetchAll = useCallback(() => {
    GET_ApprovalToMe(null, null, (v) => {
      if (!v) setToMeReady(true);
    });
    GET_ApprovalByMe(null, null, (v) => {
      if (!v) setByMeReady(true);
    });
  }, [GET_ApprovalToMe, GET_ApprovalByMe]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const toMeRaw = _.get(props, "toMe", []);
  const byMeRaw = _.get(props, "byMe", []);

  const apToMe = useMemo(() => buildToMeRows(toMeRaw), [toMeRaw]);

  const apByMe = useMemo(
    () =>
      (Array.isArray(byMeRaw) ? byMeRaw : _.get(byMeRaw, "data_list", []) || []).map((r, i) => {
        const stKey = reviewStatusKey(pick(r, "status", "review_status", "reviewStatus", "Status"));
        /*
         * status 這個欄位後端兩種都給得出來：/Approval/ByMe 現在回中文字串（「已退回」），
         * 但 DTO 上它是列舉、也可能回數字代碼。只有字串才是能直接顯示的標籤，
         * 數字要走 REVIEW_STATUS_LABEL 轉換，否則標籤上會出現「2」這種裸代碼
         * （原本寫成 `pick(...) || REVIEW_STATUS_LABEL[stKey]`，數字是 truthy，永遠輪不到後面的轉換）。
         */
        const stRaw = pick(r, "status_label", "statusLabel", "status", "st");
        const stText = typeof stRaw === "string" ? stRaw : REVIEW_STATUS_LABEL[stKey] || "待審";
        const pending = stKey === "pending";
        const w = pending ? waitOf(r) : null;
        const id = pick(r, "id", "review_id", "reviewId", "Id");
        const rv = reviewersOf(r);
        const decidedAt = pick(
          r,
          "decided_at",
          "decidedAt",
          "closed_at",
          "closedAt",
          "update_at",
          "updated_at",
          "UpdateAt"
        );

        /* 同上：人 → 等多久了／結果，第一格一樣是人，不放代碼 */
        const metas = [];
        if (rv) metas.push(`審核人：${rv}`);
        if (pending && w && w.days !== null) {
          metas.push(w.days === 0 ? "今日送出" : `已送出 ${w.days} 天，尚未審核`);
        }
        // 已經有結果的，寫成「9 月 12 日通過」一句話；沒回決定時間就只留標籤，不掰一個日期出來
        if (!pending && decidedAt) metas.push(`${fmtDay(decidedAt)}${stText}`);

        return {
          key: id != null ? `by-${id}` : `by-${i}`,
          taskId: taskIdOf(r),
          title: String(pick(r, "task_title", "taskTitle", "title", "name", "Title") || "—"),
          type: reviewTypeLabel(pick(r, "review_type", "reviewType", "type", "ReviewType")),
          metas,
          tag: pending
            ? w && { text: w.text, tone: w.tone, mark: w.tone === TONE_ATTENTION ? <MdWarningAmber /> : null }
            : {
                text: stText,
                tone: ST_TONE[stKey] || TONE_CLOSED,
                mark: stKey === "approved" ? <MdCheck /> : null,
              },
          // 被退回的要跟逾期一樣看得出來：那是「等你回去改」，不是一筆已經結束的紀錄
          attention: pending
            ? !!w && w.tone === TONE_ATTENTION
            : stKey === "rejected" || stKey === "returned",
        };
      }),
    [byMeRaw]
  );

  const toMeCount = apToMe.length;
  const byMeCount = apByMe.length;

  /*
   * 分頁順序：有東西的排前面（設計稿 L1193 —— 範例裡「等我審的」是 0，所以排在後面）。
   * sort 在現行 JS 是穩定的，兩邊都是 0 時維持原順序（等我審的在前）。
   */
  const orderedTabs = useMemo(() => {
    const defs = [
      { key: TAB_TOME, label: "待我審核", count: toMeCount },
      { key: TAB_BYME, label: "我送出的審核", count: byMeCount },
    ];
    return defs.slice().sort((a, b) => (b.count > 0) - (a.count > 0));
  }, [toMeCount, byMeCount]);

  /*
   * 目前分頁：網址明確帶了 ?tab= 就照網址（使用者點過、或重新整理／上一頁回來）；
   * 沒帶才自動挑排在前面那一個。
   *
   * 自動結果只決定一次。若每次重繪都重算，使用者在「等我審的」把最後一筆審完、清單一變空，
   * 畫面就會自己跳到另一個分頁，像是按錯了什麼。
   * 在 render 階段設定而不用 effect：避免先畫一格錯的分頁再跳過去。
   */
  const urlTab = searchParams.get("tab");
  const explicitTab = TAB_KEYS.includes(urlTab) ? urlTab : null;
  const [autoTab, setAutoTab] = useState(null);
  if (autoTab === null && (toMeCount > 0 || (toMeReady && byMeReady))) {
    setAutoTab(orderedTabs[0].key);
  }
  const activeTab = explicitTab || autoTab || TAB_TOME;

  /* 點分頁就寫進網址。用 replace：切分頁不該在瀏覽器歷史裡一格一格留下來，按上一頁要能直接離開 */
  const selectTab = useCallback(
    (key) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const openRow = useCallback((row) => navigate(`/Approvals/${row.taskId}`), [navigate]);

  const showMore = useCallback(
    (key) => setShown((s) => ({ ...s, [key]: s[key] + PAGE_SIZE })),
    []
  );

  const onTome = activeTab === TAB_TOME;
  const list = onTome ? apToMe : apByMe;
  const listReady = onTome ? toMeReady : byMeReady;
  const shownN = shown[activeTab];

  /*
   * 為什麼現在看到的是這一份 —— 設計稿 L1197-L1200。
   * 沒有這句話，使用者會以為自己點錯分頁，或以為另一份不見了。
   * 等「等我審的」載完才寫，否則會在資料回來前先斷言「現在沒有要你審的東西」。
   */
  let bannerText = "";
  if (toMeReady) {
    if (onTome) {
      bannerText =
        toMeCount > 0
          ? `共有 ${toMeCount} 件待您審核，因此優先顯示此清單。`
          : "目前沒有待您審核的項目。有人送審時將顯示於此，並自動排列於前。";
    } else {
      bannerText =
        toMeCount > 0
          ? `此為您送出的審核；另有 ${toMeCount} 件待您審核，請記得切換至「待我審核」處理。`
          : "目前沒有待您審核的項目，因此優先顯示您送出的審核。有人送審時，「待我審核」將自動排列於前。";
    }
  }

  let body = null;
  if (!listReady && list.length === 0) {
    // 還在讀：不要先畫一個空框，也不要先說「沒有」——那時只是資料還沒回來
    body = <SkeletonRows rows={4} sx={{ mt: 3 }} />;
  } else if (list.length === 0) {
    body = (
      <EmptyState
        title={onTome ? "目前沒有待您審核的項目" : "尚未送出任何審核"}
        text={
          onTome
            ? "有工項送交您審核時，將顯示於此。"
            : "工項完成後，於工項頁按「送出審核」，送出的審核將列於此。"
        }
      />
    );
  } else {
    body = (
      <>
        <Card sx={{ mt: 2 }}>
          {list.slice(0, shownN).map((a) => (
            <ApprovalRow key={a.key} row={a} onOpen={openRow} />
          ))}
        </Card>
        {/* 長清單不用頁碼，用設計稿 L855 的「看其他 N 件」：不必先算自己在第幾頁 */}
        {list.length > shownN && (
          <Box sx={{ py: 2 }}>
            <Button variant="text" type="button" onClick={() => showMore(activeTab)}>
              顯示更多（{list.length - shownN}）
            </Button>
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <Typography variant="h1" component="h1">
        審核
      </Typography>

      {/* MUI Tabs 自帶 tablist / tab 角色與 aria-selected；分頁內容區另外標 tabpanel */}
      <Tabs
        value={activeTab}
        onChange={(_e, key) => selectTab(key)}
        aria-label="審核清單"
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mt: 2 }}
      >
        {orderedTabs.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={`${t.label} ${t.count}`}
            aria-controls="ap-panel"
            // 使用導覽第一步框的是 ap-tome。標記掛在分頁鈕上才永遠有地方落
            // （清單卡片會因為切分頁而不在畫面上），也順便指出要點哪裡去審核
            data-tour={t.key === TAB_TOME ? "ap-tome" : undefined}
          />
        ))}
      </Tabs>

      {bannerText && (
        <Banner kind="info" icon={<MdInfoOutline />} sx={{ mt: 2 }}>
          {bannerText}
        </Banner>
      )}

      <div id="ap-panel" role="tabpanel">
        {body}
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  toMe: _.get(state, "Approval.ToMe", []),
  byMe: _.get(state, "Approval.ByMe", []),
});

/*
 * POST_ApprovalDecide 不再由這一頁派送：決議連同審核意見一起在 /Approvals/{taskId} 送出。
 * 清單上原本那兩顆「通過／要求修改」是不填意見的快捷鍵，而整列現在就是一顆按鈕
 * （HTML 不允許按鈕裡再放按鈕），設計稿也要求一頁只有一顆主要按鈕。
 */
const mapDispatchToProps = (dispatch) => ({
  GET_ApprovalToMe: (payload, callback, loading) =>
    dispatch({ type: "GET_ApprovalToMe", payload, callback, loading }),
  GET_ApprovalByMe: (payload, callback, loading) =>
    dispatch({ type: "GET_ApprovalByMe", payload, callback, loading }),
});

Approvals.propTypes = {
  toMe: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  byMe: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  GET_ApprovalToMe: PropTypes.func,
  GET_ApprovalByMe: PropTypes.func,
};

Approvals.defaultProps = {
  toMe: [],
  byMe: [],
  GET_ApprovalToMe: () => {},
  GET_ApprovalByMe: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(Approvals);
